import { CounterpartyLookupDetails } from "../../../api/Api";

/**
 * Which warehouse source a row came from. The dialog groups by this because Compliance and Risk
 * each recognise their own system of record, and the two are queried independently, so one can be
 * missing while the other is present.
 */
export type SyncSource = "compliance" | "risk";

/**
 * All three books sync the same four fields from the same warehouse rows, but the credit limit does
 * not mean the same thing for all of them, so the comparison needs to know which it is looking at.
 * See deriveCreditCheck.
 *
 * A country is one entity with several ids, one per party it depends on, so a "country" here is
 * really one (country, party) pair. Which party it is never reaches this file: what differs per
 * party is where the values are read from and written to, and that is the caller's problem.
 */
export type SyncEntityType = "counterparty" | "broker" | "country";

export type SyncableField =
  | "kycCheck"
  | "creditCheck"
  | "creditAmount"
  | "creditCurrency";

export type HubSyncValues = {
  kycCheck: string;
  creditCheck: string;
  creditAmount: number | undefined;
  creditCurrency: string;
};

export type SyncRow = {
  field: SyncableField;
  label: string;
  source: SyncSource;
  hubDisplay: string;
  warehouseDisplay: string;
  /** The value to write into the lifecycle when this row is accepted. */
  nextValue: string | number | undefined;
  differs: boolean;
  /** False when there is nothing the sync can write. See `unsyncable` for why. */
  syncable: boolean;
  /** Present exactly when `syncable` is false. */
  unsyncable?: Unsyncable;
};

/**
 * Why a row cannot be synced. There are two classes of reason and they read completely differently,
 * which is the whole point of splitting them:
 *
 *   "expected"   nothing to copy, and nothing is wrong. A warehouse limit of NULL means unlimited,
 *                a country bank has no credit line, Compliance marked a party not needed. These are
 *                answers, not problems, and the page must not ask anyone to look into them.
 *   "attention"  the warehouse holds something we have no way to store, so a human has to decide
 *                what it should have been.
 *
 * They used to render identically, as an amber "Cannot sync" with the reason buried in a tooltip.
 * That reliably read as a fault: the commonest case by far is an unlimited credit line, which is
 * both completely normal and already carried by the credit check row, and people asked Risk why
 * their data would not sync. The label now says what is actually going on, in the cell.
 */
export type UnsyncableKind = "expected" | "attention";

export type Unsyncable = {
  kind: UnsyncableKind;
  /** Short, and specific enough to need no follow-up question. Shown in the cell. */
  label: string;
  /** The whole sentence. Shown in the tooltip, and inline in the single-entity dialog. */
  reason: string;
};

const expected = (label: string, reason: string): Unsyncable => ({
  kind: "expected",
  label,
  reason
});

const needsAttention = (label: string, reason: string): Unsyncable => ({
  kind: "attention",
  label,
  reason
});

/**
 * Moody's assessment vocabulary mapped onto the Hub's KYC check enum. Keys are lower-cased so a
 * casing change upstream does not silently stop matching.
 *
 * Every value present in prod today is covered except "Error", which is deliberately absent: it
 * describes a failed assessment, not an outcome, and has no Hub equivalent. Unmapped values
 * surface in the dialog as unsyncable rather than being guessed at.
 */
const KYC_CHECK_BY_ASSESSMENT_STATUS: Record<string, string> = {
  accepted: "approved",
  rejected: "rejected",
  "in progress": "initiated",
  "approval in progress": "initiated"
};

export const mapAssessmentStatusToKycCheck = (
  status?: string | null
): string | null => {
  if (!status) return null;
  return KYC_CHECK_BY_ASSESSMENT_STATUS[status.trim().toLowerCase()] ?? null;
};

const KYC_CHECK_LABELS: Record<string, string> = {
  initiated: "Initiated",
  rejected: "Rejected",
  approved: "Approved"
};

/**
 * The credit check outcome, derived from the limit rather than read from a column: the warehouse
 * has no approval flag, and AssessmentType is the method used, not a decision.
 *
 * **Countries follow the counterparty rule**, deliberately. A country's credit line towards its TSO
 * or its exchange is real exposure assessed the same way a counterparty's is, so a 0 limit means the
 * same thing there that it means for a counterparty. The broker carve-out below is about brokers not
 * carrying a real limit at all, which does not apply.
 *
 * Confirmed with Risk (ADHO-490), for **trading counterparties**:
 *   M_CreditLimit NULL     no limit, i.e. unlimited  -> approved
 *   M_CreditLimit 0        no exposure permitted     -> rejected
 *   M_CreditLimit positive limit granted             -> approved
 *
 * Note NULL means "unlimited", not "not filled in". Reading it the other way round would mark the
 * 133 unlimited counterparties in prod as rejected.
 *
 * **Brokers are different: a zero limit is not a rejection.** Brokers do not carry a real credit
 * limit, so 0 there means "no limit applies", not "no exposure permitted". An assessed broker is
 * approved whatever the number says, and only the absence of an assessment leaves it undecided.
 * Applying the counterparty rule to brokers would mass-reject every assessed broker with a 0 row.
 *
 * This changes the derived *outcome* only. The credit amount still copies the column verbatim for
 * both, 0 included: the Hub is meant to match the warehouse 1:1 on the values it stores.
 *
 * Returns null when the row does not look like a real assessment, so a degenerate all-null risk row
 * can never derive an approval. AssessmentType is NOT NULL on every one of the 341 rows in prod, so
 * this guard never rejects real data.
 */
export const deriveCreditCheck = (
  details: CounterpartyLookupDetails,
  entityType: SyncEntityType
): string | null => {
  if (!details.assessmentType?.trim()) return null;
  if (entityType === "broker") return "approved";
  return details.creditLimit === 0 ? "rejected" : "approved";
};

/**
 * A status the Hub holds that a sync must never overwrite, however different the warehouse is.
 *
 * Only countries can hold one: "not needed" is how Compliance says it does not screen a given party,
 * and it is a decision somebody made, not a blank waiting to be filled. Offering to replace it with a
 * Moody's assessment would quietly undo that decision on the next bulk sweep. Compliance changes it
 * in the dialog if they want the party screened after all, and the sync picks it up from then on.
 */
const isDecidedNotToSync = (hubKycCheck: string) =>
  hubKycCheck === "not_needed";

const NOT_SET = "Not set";

const displayEnum = (value?: string | null) =>
  value ? (KYC_CHECK_LABELS[value] ?? value) : NOT_SET;

const displayAmount = (value?: number | null) =>
  value === undefined || value === null
    ? NOT_SET
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Amounts on the warehouse side, where a null limit is known to mean unlimited rather than missing.
 * The Hub side keeps NOT_SET, because a blank Hub amount only means unlimited once the credit check
 * says approved, which the credit check row above already reports.
 */
const displayWarehouseAmount = (value?: number | null) =>
  value === undefined || value === null ? "Unlimited" : displayAmount(value);

const displayText = (value?: string | null) =>
  value && value.trim() ? value : NOT_SET;

/**
 * Options for the books whose shape is not the counterparty one.
 */
export type SyncRowOptions = {
  /**
   * False when the entity has no credit check, amount or currency field to write into, so the three
   * risk rows are offered as unsyncable rather than as differences. Countries pass false for every
   * party except the TSO and the exchange: those are the only two a country takes credit exposure
   * to, and CountryCreditRiskLifecycle has fields for exactly those two.
   *
   * The rows are still produced, so the table keeps one column per field and can say why the cell is
   * empty. Dropping them would render as "No data", which is the warehouse's answer, not the Hub's.
   */
  supportsCredit?: boolean;
};

/**
 * Builds the diff rows for one warehouse lookup. Pure, so the mapping rules can be reasoned about
 * without a running dialog.
 *
 * A row is only produced when its source actually returned data: reading a null credit limit off an
 * absent risk row and offering to write it over a Hub value would be destructive, not a sync.
 */
export const buildSyncRows = (
  details: CounterpartyLookupDetails,
  hub: HubSyncValues,
  entityType: SyncEntityType,
  options: SyncRowOptions = {}
): SyncRow[] => {
  const rows: SyncRow[] = [];
  const supportsCredit = options.supportsCredit !== false;

  if (details.complianceDataAvailable) {
    const mapped = mapAssessmentStatusToKycCheck(details.assessmentStatus);
    const decidedNotToSync = isDecidedNotToSync(hub.kycCheck);
    const syncable = mapped !== null && !decidedNotToSync;

    rows.push({
      field: "kycCheck",
      label: "KYC check",
      source: "compliance",
      hubDisplay: displayEnum(hub.kycCheck),
      warehouseDisplay:
        mapped !== null
          ? `${displayEnum(mapped)} (${details.assessmentStatus})`
          : displayText(details.assessmentStatus),
      nextValue: mapped ?? undefined,
      differs: syncable && mapped !== (hub.kycCheck || null),
      syncable,
      unsyncable: decidedNotToSync
        ? expected(
            "Marked not needed",
            "Compliance marked this party as not needing a KYC check, so the sync leaves it alone. Change that in the Compliance dialog and it will sync from then on."
          )
        : syncable
          ? undefined
          : needsAttention(
              "Unrecognised status",
              `The warehouse says "${details.assessmentStatus}", which has no matching Hub status. Somebody has to decide what it should be.`
            )
    });
  }

  if (details.riskDataAvailable) {
    const derivedCheck = deriveCreditCheck(details, entityType);

    // Shown on all three risk rows when the entity has no credit line at all, in place of whatever
    // the row would otherwise have said about the warehouse value. See SyncRowOptions.
    const noCreditField = supportsCredit
      ? undefined
      : expected(
          "Not applicable",
          "A country holds credit towards its TSO and its exchange only, so there is no field on this party to sync into. Whatever the warehouse holds for it is not wrong, it just has nowhere to go."
        );

    rows.push({
      field: "creditCheck",
      label: "Credit check",
      source: "risk",
      hubDisplay: displayEnum(hub.creditCheck),
      warehouseDisplay: derivedCheck
        ? `${displayEnum(derivedCheck)} (${
            details.creditLimit === null || details.creditLimit === undefined
              ? "no limit"
              : `limit ${displayAmount(details.creditLimit)}`
          })`
        : NOT_SET,
      nextValue: derivedCheck ?? undefined,
      differs:
        supportsCredit &&
        derivedCheck !== null &&
        derivedCheck !== (hub.creditCheck || null),
      syncable: supportsCredit && derivedCheck !== null,
      unsyncable:
        noCreditField ??
        (derivedCheck === null
          ? needsAttention(
              "No assessment",
              "The risk row carries no assessment type, so there is no outcome to derive. That is not a shape real warehouse data takes, so it is worth looking at."
            )
          : undefined)
    });

    // The amount mirrors the warehouse column 1:1 for both entity types, including a literal 0.
    // Only NULL has nothing to write, because there is no number there to copy.
    //
    // So a broker on a 0 limit syncs as approved (see deriveCreditCheck) with an amount of 0. That
    // pairing looks odd read as "approved for nothing", but the two fields are answering different
    // questions and the Hub is meant to show what the warehouse holds.
    const warehouseAmount = details.creditLimit ?? undefined;
    rows.push({
      field: "creditAmount",
      label: "Credit amount",
      source: "risk",
      hubDisplay: displayAmount(hub.creditAmount),
      warehouseDisplay: displayWarehouseAmount(details.creditLimit),
      nextValue: warehouseAmount,
      differs: supportsCredit && warehouseAmount !== hub.creditAmount,
      syncable: supportsCredit && warehouseAmount !== undefined,
      // A null limit means unlimited, so there is no number to write. The Hub cannot express
      // "unlimited" in an amount field, and the credit check row above already carries that meaning.
      unsyncable:
        noCreditField ??
        (warehouseAmount === undefined
          ? expected(
              "Nothing to copy",
              "The warehouse holds no limit, which means unlimited rather than missing. There is no number to put in this field, and the credit check above already carries the meaning: it syncs as approved off this same value."
            )
          : undefined)
    });

    const warehouseCurrency = details.currency?.trim() || undefined;
    rows.push({
      field: "creditCurrency",
      label: "Credit currency",
      source: "risk",
      hubDisplay: displayText(hub.creditCurrency),
      warehouseDisplay: displayText(details.currency),
      nextValue: warehouseCurrency,
      differs:
        supportsCredit &&
        (warehouseCurrency ?? "") !== (hub.creditCurrency || ""),
      syncable: supportsCredit && warehouseCurrency !== undefined,
      unsyncable:
        noCreditField ??
        (warehouseCurrency === undefined
          ? expected(
              "Nothing to copy",
              "The credit assessment has no currency set, so there is nothing to copy into this field."
            )
          : undefined)
    });
  }

  return rows;
};

/** The rows the user can actually act on: a real difference, and a value we know how to write. */
export const isActionable = (row: SyncRow) => row.differs && row.syncable;

/**
 * Collapses the accepted rows into a patch for the lifecycle form. Only accepted fields appear, so
 * the caller can spread it without clobbering anything the user left unchecked.
 */
export const buildPatch = (
  rows: SyncRow[],
  accepted: ReadonlySet<SyncableField>
): Partial<HubSyncValues> => {
  const patch: Partial<HubSyncValues> = {};

  rows.forEach((row) => {
    if (!isActionable(row) || !accepted.has(row.field)) return;

    if (row.field === "creditAmount") {
      patch.creditAmount = row.nextValue as number | undefined;
    } else if (row.field === "kycCheck") {
      patch.kycCheck = (row.nextValue as string | undefined) ?? "";
    } else if (row.field === "creditCheck") {
      patch.creditCheck = (row.nextValue as string | undefined) ?? "";
    } else {
      patch.creditCurrency = (row.nextValue as string | undefined) ?? "";
    }
  });

  return patch;
};
